import {css} from '@emotion/css';
import {Switch, Match} from 'solid-js';
import {ImageSchema, ImageSize, Url} from 'Shared/model/basic';
import { useUrls } from 'Shared/UrlProvider';
import { useBuild, useSite } from 'Shared/SiteProvider';
import { rawImageUrl } from 'Common/Urls';


interface IImagePreview {
	folder:string;
	size?:ImageSize;
	format: string;
	image:ImageSchema;
	node: ()=>HTMLElement;
	setNode: (e:HTMLElement)=>void;

	ratio?: number;
	fallbackUrl?:Url;
	shared?: boolean;

	/* Width in px of the preview, and of the placeholder standing in for it. Left out, the
	   placeholder takes its old 300px and the image is sized by whatever styles the caller wraps
	   it in. */
	width?: number;
}


export function ImagePreview(P:IImagePreview)
{
	const previewStyle = css({
		position: 'relative',
		display: 'inline-block',

		/* Doubled class: the styles wrapped around the uploader carry 'img' rules of their own, and
		   those are no less specific, so this has to win on its own terms rather than on order. */
		'&& img': {
			maxWidth: P.width,
			maxHeight: P.width
		}
	});

	/* checkerboard background to the preview so transparent pngs containing 100% white images are visible */
	const checkerboardStyle = css({
		backgroundSize: '40px 40px',
		backgroundImage: `
		  linear-gradient(45deg, #f0f4f9 25%, transparent 25%),
		  linear-gradient(-45deg, #f0f4f9 25%, transparent 25%),
		  linear-gradient(45deg, transparent 75%, #f0f4f9 75%),
		  linear-gradient(-45deg, transparent 75%, #f0f4f9 75%)
		`,
		// The base background color (Color 2)
		backgroundColor: '#dedede',
		// Precise alignment for the checkerboard grid
		backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
	});

	const loadingStyle = css({
		position: 'absolute',
		display: 'flex',
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		alignItems: 'center',
		justifyContent: 'center',

		img: {
			color: 'white',
			width: 60,
			height: 60,
			alignSelf: 'center'
		}
	});

	const fallbackWidth = P.width ?? 300;

	const fallbackStyle = css({
		border: 'solid 10px #e1e1e1',
		width: fallbackWidth,
		height: `calc(${fallbackWidth}px / ${P.ratio})`,

		backgroundImage: `repeating-linear-gradient(
			-125deg, 
			#e1e1e1 0,
			#e1e1e1 10px, 
			#ffffff 10px, 
			#ffffff 25px 
		)`
	});

	const size = () => P.size ?? 'full';
	const urls = useUrls();
	const site = useSite();
	const build = useBuild();
	const loadingUrl = urls.resourceUrl('loading.gif');
	const previewUrl = () => rawImageUrl(build,P.shared ?? false,site.key ?? 'artists',P.folder,P.image.hash,size(),P.image.formats[size()].format,P.image.dev,false);

	return (
		<div class={previewStyle}>
			<Switch>
				<Match when={P.image?.dataUrl}>
					<img alt='Image preview' src={P.image.dataUrl} />
					<div class={loadingStyle}>
						<img class={checkerboardStyle} alt='Loading' src={loadingUrl} />
					</div>
				</Match>
				<Match when={P.image?.hash}>
					<img class={checkerboardStyle} alt='Preview' src={previewUrl()} onClick={() => P.node().click()}/> 
				</Match>
				<Match when={P.ratio}>
					<div class={fallbackStyle} onClick={() => P.node().click()} />
				</Match>
				<Match when={P.fallbackUrl}>
					<img class={checkerboardStyle} alt='Preview' src={urls.resourceUrl(P.fallbackUrl)} onClick={() => P.node().click()} /> 
				</Match>
			</Switch>
		</div>
	);
}

